import React from "react";
import { Box, Typography } from "@mui/material";
import designTokens, { colors, typography } from "../../styles/designTokens";
import ConversionStepMarker, {
  type ConversionStepState,
} from "./ConversionStepMarker";

export interface ConversionStep {
  key: string;
  label: string;
  state: ConversionStepState;
  /** 라벨 색 override. 실패 단계처럼 시안이 별도 색을 지정할 때만 쓴다. */
  labelColor?: string;
  /** 라벨 오른쪽 끝에 붙는 보조 표시 (경과 시간, `Stopped here`). */
  trailing?: React.ReactNode;
  /** 라벨 아래 설명. 이유 문장이 길어지면 rail이 따라 늘어난다. */
  detail?: React.ReactNode;
}

const stepTokens = designTokens.semantic.conversionReport.step;

/**
 * 변환 단계 목록.
 *
 * View R UI `2904:98915` 규격. Marker 24 / gap 12 / Label row 24 / Content
 * 하단 여백 16이며, 마커를 잇는 rail은 **auto-layout 자식**이다. 절대 좌표로
 * 그리면 이유 문장이 길어질 때 다음 마커까지 닿지 않는다. 마지막 행에는 rail을
 * 두지 않는다.
 */
const ConversionSteps: React.FC<{
  steps: ConversionStep[];
  "data-testid"?: string;
}> = ({ steps, "data-testid": testId }) => (
  <Box
    data-testid={testId}
    sx={{ display: "flex", flexDirection: "column", width: "100%" }}
  >
    {steps.map((step, index) => {
      const isLast = index === steps.length - 1;
      return (
        <Box
          key={step.key}
          data-stage={step.key}
          data-stage-state={step.state}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: stepTokens.columnGap,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              alignSelf: "stretch",
              width: `${stepTokens.dotSize}px`,
              flexShrink: 0,
            }}
          >
            <ConversionStepMarker
              size={stepTokens.dotSize}
              stepNumber={index + 1}
              state={step.state}
            />
            {!isLast && (
              <Box
                aria-hidden="true"
                data-conversion-step-rail="true"
                sx={{
                  flex: 1,
                  minHeight: stepTokens.rowGap,
                  width: `${stepTokens.railWidth}px`,
                  borderRadius: `${stepTokens.railWidth}px`,
                  bgcolor:
                    step.state === "complete"
                      ? designTokens.fill.shape.primary50.dark
                      : stepTokens.railPending,
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flex: 1,
              minWidth: 0,
              flexDirection: "column",
              gap: designTokens.numbers.gap[4],
              pb: isLast ? 0 : stepTokens.rowGap,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: stepTokens.columnGap,
                height: `${stepTokens.dotSize}px`,
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  ...typography.styles.viewrStudio.bodyRegular,
                  color:
                    step.labelColor ??
                    (step.state === "pending"
                      ? stepTokens.pending
                      : colors.grey[80].dark),
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {step.label}
              </Typography>
              {step.trailing}
            </Box>
            {step.detail}
          </Box>
        </Box>
      );
    })}
  </Box>
);

export default ConversionSteps;
